import { Box, IconButton, Link as MuiLink, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import type { TimeRange } from "../../utils/time";
import { ContentCopy, FileCopy, FileCopyOutlined } from "@mui/icons-material";

function truncate(value: string, truncateAt: number): string {
  if (truncateAt <= 0) return value;
  return value.length > truncateAt ? `${value.slice(0, truncateAt)}…` : value;
}

function emptyCell(): ReactNode {
  return (
    <Typography component="span" variant="body2" sx={{ opacity: 0.5 }}>
      -
    </Typography>
  );
}

function textCell(value: string, truncateAt: number): ReactNode {
  return (
    <Typography component="span" variant="body2" className="om-text-mono" sx={{ opacity: 0.85 }}>
      {truncate(value, truncateAt)}
    </Typography>
  );
}

export function IdLink({
  to,
  value,
  truncateAt = 16,
  title,
}: {
  to: string;
  value: string | null | undefined;
  truncateAt?: number;
  title?: string;
}) {
  if (!value) return emptyCell();

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      
    }
  };

  const text = value; truncate(value, truncateAt);

  // Use RouterLink (anchor under the hood) so middle-click / open-in-new-tab works.
  // copy button visible only on hover
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, "&:hover": { ".MuiIconButton-root": { opacity: 1 } } }}> 
      <MuiLink
        component={RouterLink}
        to={to}
        underline="hover"
        sx={{ textTransform: "none" }}
        title={title ?? value}
        >
        <Typography component="span" variant="body2" fontFamily="monospace" sx={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
          {text}
        </Typography>
      </MuiLink>
      <IconButton onClick={handleCopy} size="small" color="primary" sx={{ opacity: 0, padding: 0, transition: "opacity 0.2s ease-in-out" }}>
        <ContentCopy fontSize="small" />
      </IconButton>
    </Box>
  );
}

export function TraceIdLink({
  serviceSlug,
  traceId,
  truncateAt = 16,
}: {
  serviceSlug: string | null | undefined;
  traceId: string | null | undefined;
  truncateAt?: number;
}) {
  if (!traceId) return emptyCell();
  if (!serviceSlug) return textCell(traceId, truncateAt);
  const to = `/services/${encodeURIComponent(serviceSlug)}/traces/${encodeURIComponent(traceId)}`;
  return <IdLink to={to} value={traceId} truncateAt={truncateAt} />;
}

function buildRangeQuery(range: TimeRange | null | undefined, rangeSearchParams?: URLSearchParams) {
  const params = new URLSearchParams();

  const fromRaw = rangeSearchParams?.get("range_from");
  const stopRaw = rangeSearchParams?.get("range_stop");

  if (fromRaw) params.set("range_from", fromRaw);
  if (stopRaw) params.set("range_stop", stopRaw);

  return params;
}

export function SpanIdLink({
  serviceSlug,
  spanId,
  truncateAt = 16,
  range,
  rangeSearchParams,
}: {
  serviceSlug: string | null | undefined;
  spanId: string | null | undefined;
  truncateAt?: number;
  range?: TimeRange | null;
  rangeSearchParams?: URLSearchParams;
}) {
  if (!spanId) return emptyCell();
  if (!serviceSlug) return textCell(spanId, truncateAt);

  const params = buildRangeQuery(range ?? null, rangeSearchParams);
  const qs = params.toString();
  const to = `/services/${encodeURIComponent(serviceSlug)}/spans/${encodeURIComponent(spanId)}${
    qs ? `?${qs}` : ""
  }`;
  return <IdLink to={to} value={spanId} truncateAt={truncateAt} />;
}

export function LogIdLink({
  serviceSlug,
  logId,
  truncateAt = 8,
}: {
  serviceSlug: string | null | undefined;
  logId: string | null | undefined;
  truncateAt?: number;
}) {
  if (!logId) return emptyCell();
  if (!serviceSlug) return textCell(logId, truncateAt);
  const to = `/services/${encodeURIComponent(serviceSlug)}/logs/${encodeURIComponent(logId)}`;
  return <IdLink to={to} value={logId} truncateAt={truncateAt} />;
}
